import React from 'react';

interface SkeletonProps {
  className?: string;
}

/**
 * Primitive skeleton block.
 * Usage: <Skeleton className="h-4 w-32" />
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-800 rounded ${className}`} />
);

/**
 * One skeleton row for table-like layouts (Dashboard).
 */
export const SkeletonRow: React.FC = () => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
    <div className="flex items-center gap-4 flex-1 min-w-0">
      <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton className="h-4 w-48 max-w-full" />
        <Skeleton className="h-3 w-32 max-w-full" />
      </div>
    </div>
    <div className="hidden sm:flex items-center gap-6">
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  </div>
);

/**
 * One skeleton card for card grids.
 */
export const SkeletonCard: React.FC = () => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 space-y-4">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-lg" />
      <Skeleton className="h-4 w-32" />
    </div>
    <Skeleton className="h-3 w-full" />
    <Skeleton className="h-3 w-5/6" />
    <Skeleton className="h-3 w-4/6" />
  </div>
);

/**
 * Multiple skeleton rows. Default 5.
 */
export const SkeletonList: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonRow key={i} />
    ))}
  </>
);

export default Skeleton;
